<template>
  <div class="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50">
    <div class="absolute inset-0 flex items-end sm:items-center justify-center">
      <div
        class="bg-white dark:bg-gray-800 w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-lg shadow-xl flex flex-col"
      >
        <div
          class="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0"
        >
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">
            New Customer
          </h2>
          <button
            @click="$emit('close')"
            class="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-4 space-y-6">
          <div class="space-y-4">
            <h3
              class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
            >
              Basic Information
            </h3>

            <div>
              <TextInput
                v-model="fields.name.value"
                label="Customer Name"
                :error="fields.name.error"
                placeholder="Enter customer name"
                :required="true"
              />
            </div>

            <div>
              <SearchSelect
                v-model="fields.customer_types_id.value"
                :options="customerTypeOptions"
                label="Customer Type"
                :error="fields.customer_types_id.error"
                placeholder="Select customer type"
                :required="true"
              />
            </div>

            <div>
              <SearchSelect
                v-model="fields.customer_groups_id.value"
                :options="customerGroupOptions"
                @select="handleCustomerType"
                label="Customer Group"
                :error="fields.customer_groups_id.error"
                placeholder="Select customer Group"
              />
            </div>

            <div>
              <PhoneInput
                v-model="fields.mobile.value"
                :required="true"
                label="Mobile"
                :error="fields.mobile.touched ? fields.mobile.error : null"
                @blur="fields.mobile.touched = true"
              />
            </div>

            <div>
              <SearchSelect
                v-model="fields.countrys_id.value"
                :options="countryOptions"
                label="Country"
                :error="fields.countrys_id.error"
                placeholder="Select country"
              />
            </div>

            <div>
              <GroupedSelect
                v-model="fields.states_id.value"
                label="State"
                :hasExtra="true"
                optionLabel="states"
                :extra="fields.countrys_id.value"
                end-point="system/master-data/states"
                :error="
                  fields.states_id.touched ? fields.states_id.error : null
                "
                @blur="fields.states_id.touched = true"
                :allowNewRecord="true"
              />
            </div>

            <div>
              <GroupedSelect
                v-model="fields.citys_id.value"
                label="City"
                :hasExtra="true"
                :extra="fields.states_id.value"
                end-point="system/master-data/citys"
                :error="fields.citys_id.touched ? fields.citys_id.error : null"
                optionLabel="citys"
                @blur="fields.citys_id.touched = true"
                :allowNewRecord="true"
              />
            </div>

            <div>
              <GroupedSelect
                v-model="fields.roads_id.value"
                label="Road"
                optionLabel="roads"
                :hasExtra="true"
                :extra="fields.citys_id.value"
                end-point="system/master-data/roads"
                :error="fields.roads_id.touched ? fields.roads_id.error : null"
                @blur="fields.roads_id.touched = true"
                :allowNewRecord="true"
              />
            </div>

            <div>
              <GroupedSelect
                v-model="fields.buildings_id.value"
                label="Building"
                :hasExtra="true"
                optionLabel="buildings"
                :remote="true"
                :extra="fields.citys_id.value"
                end-point="system/master-data/buildings"
                :error="
                  fields.buildings_id.touched ? fields.buildings_id.error : null
                "
                @blur="fields.buildings_id.touched = true"
                :allowNewRecord="true"
              />
            </div>

            <div>
              <TextAreaInput
                v-model="fields.address.value"
                label="Address"
                :rows="3"
                :error="fields.address.touched ? fields.address.error : null"
                @blur="fields.address.touched = true"
              />
            </div>
          </div>

          <div v-if="showAdvanced" class="space-y-6">
            <div class="space-y-4">
              <h3
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >
                Contact Information
              </h3>

              <div>
                <PhoneInput
                  v-model="fields.mobile.value"
                  :required="true"
                  label="Mobile"
                  :error="fields.mobile.touched ? fields.mobile.error : null"
                  @blur="fields.mobile.touched = true"
                />
              </div>

              <div>
                <TextInput
                  v-model="fields.email.value"
                  :required="true"
                  label="Email"
                  :error="fields.email.touched ? fields.email.error : null"
                  @blur="fields.email.touched = true"
                />
              </div>

              <div>
                <TextInput
                  v-model="fields.url.value"
                  :required="true"
                  label="Url"
                  :error="fields.url.touched ? fields.url.error : null"
                  @blur="fields.url.touched = true"
                />
              </div>
            </div>

            <div class="space-y-4">
              <h3
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >
                Business Details
              </h3>

              <div>
                <TextInput
                  v-model="fields.registration_no.value"
                  :required="true"
                  label="Registration No"
                  :error="
                    fields.registration_no.touched
                      ? fields.registration_no.error
                      : null
                  "
                  @blur="fields.registration_no.touched = true"
                />
              </div>

              <div>
                <TextInput
                  v-model="fields.tax_registration_no.value"
                  :required="true"
                  label="Tax Registration No"
                  :error="
                    fields.tax_registration_no.touched
                      ? fields.tax_registration_no.error
                      : null
                  "
                  @blur="fields.tax_registration_no.touched = true"
                />
              </div>
            </div>

            <div class="space-y-4">
              <h3
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >
                Payment Settings
              </h3>

              <div>
                <SearchSelect
                  v-model="fields.price_lists_id.value"
                  label="Price List"
                  end-point="finance/master-data/price-lists"
                  :error="
                    fields.price_lists_id.touched
                      ? fields.price_lists_id.error
                      : null
                  "
                  @blur="fields.price_lists_id.touched = true"
                  :allowNewRecord="true"
                />
              </div>

              <div>
                <SearchSelect
                  v-model="fields.credit_terms_id.value"
                  label="Credit Term"
                  end-point="finance/master-data/credit-terms"
                  :error="
                    fields.credit_terms_id.touched
                      ? fields.credit_terms_id.error
                      : null
                  "
                  @blur="fields.credit_terms_id.touched = true"
                  :allowNewRecord="true"
                />
              </div>

              <div>
                <SearchSelect
                  v-model="fields.payment_types_id.value"
                  label="Payment Type"
                  end-point="finance/master-data/payment-types"
                  :error="
                    fields.payment_types_id.touched
                      ? fields.payment_types_id.error
                      : null
                  "
                  @blur="fields.payment_types_id.touched = true"
                  :allowNewRecord="true"
                />
              </div>

              <div>
                <NumberInput
                  v-model="fields.credit_limit.value"
                  label="Credit Limit"
                  :min="0"
                  :error="
                    fields.credit_limit.touched
                      ? fields.credit_limit.error
                      : null
                  "
                  @blur="fields.credit_limit.touched = true"
                />
              </div>
            </div>

            <div class="space-y-4">
              <h3
                class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide"
              >
                Additional Settings
              </h3>

              <div>
                <SearchSelect
                  v-model="fields.accounts_receivable_id.value"
                  label="Receivable Account"
                  end-point="finance/accounting/chart-of-accounts"
                  extra="Receivables"
                  :error="
                    fields.accounts_receivable_id.touched
                      ? fields.accounts_receivable_id.error
                      : null
                  "
                  @blur="fields.accounts_receivable_id.touched = true"
                />
              </div>

              <div>
                <SearchSelect
                  v-model="fields.sales_reps_id.value"
                  label="Sales Reps"
                  end-point="revenue/master-data/sales-reps"
                  :allow-new-record="true"
                  :error="
                    fields.sales_reps_id.touched
                      ? fields.sales_reps_id.error
                      : null
                  "
                  @blur="fields.sales_reps_id.touched = true"
                />
              </div>

              <div>
                <SearchSelect
                  v-model="fields.send_invoice_via.value"
                  label="Send Invoice by"
                  :options="sendInvoiceViaList"
                  :error="
                    fields.send_invoice_via.touched
                      ? fields.send_invoice_via.error
                      : null
                  "
                  @blur="fields.send_invoice_via.touched = true"
                />
              </div>

              <div
                class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <CheckInput
                  v-model="fields.auto_reminder.value"
                  label="Auto Reminder"
                  :error="
                    fields.auto_reminder.touched
                      ? fields.auto_reminder.error
                      : null
                  "
                  @blur="fields.auto_reminder.touched = true"
                />
              </div>

              <div>
                <NumberInput
                  v-model="fields.first_reminder.value"
                  label="First Reminder (days)"
                  :readonly="!fields.auto_reminder.value"
                  :min="0"
                  :error="
                    fields.first_reminder.touched
                      ? fields.first_reminder.error
                      : null
                  "
                  @blur="fields.first_reminder.touched = true"
                />
              </div>

              <div>
                <NumberInput
                  v-model="fields.second_reminder.value"
                  label="Second Reminder (days)"
                  :readonly="!fields.auto_reminder.value"
                  :min="0"
                  :error="
                    fields.second_reminder.touched
                      ? fields.second_reminder.error
                      : null
                  "
                  @blur="fields.second_reminder.touched = true"
                />
              </div>

              <div>
                <NumberInput
                  v-model="fields.third_reminder.value"
                  label="Third Reminder (days)"
                  :readonly="!fields.auto_reminder.value"
                  :min="0"
                  :error="
                    fields.third_reminder.touched
                      ? fields.third_reminder.error
                      : null
                  "
                  @blur="fields.third_reminder.touched = true"
                />
              </div>
            </div>
          </div>

          <button
            @click="showAdvanced = !showAdvanced"
            class="w-full py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center space-x-2"
          >
            <svg
              class="w-5 h-5 transition-transform"
              :class="{ 'rotate-180': showAdvanced }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <span>{{ showAdvanced ? "Hide" : "Show" }} Advanced Options</span>
          </button>
        </div>

        <div
          class="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4 space-y-3"
        >
          <button
            @click="handleSubmit"
            :disabled="loading"
            class="w-full px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            <svg
              v-if="loading"
              class="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{{ loading ? "Saving..." : "Save Customer" }}</span>
          </button>
          <button
            @click="$emit('close')"
            class="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import Toast from "../../../../../services/Toast";
import { useFormValidation } from "../../../../../composables/useFormValidation";
import { required } from "../../../../../utils/validation";
import TextInput from "../../../../../components/ui/form/TextInput.vue";
import SearchSelect from "../../../../../components/ui/form/SearchSelect.vue";
import PhoneInput from "../../../../../components/ui/form/PhoneInput.vue";
import GroupedSelect from "../../../../../components/ui/form/GroupedSelect.vue";
import TextAreaInput from "../../../../../components/ui/form/TextAreaInput.vue";
import NumberInput from "../../../../../components/ui/form/NumberInput.vue";
import CheckInput from "../../../../../components/ui/form/CheckInput.vue";
import { toBool } from "../../../../../utils/CommonFunctions";
import { api } from "../../../../../api/Api";

interface Props {
  data?: any;
  listCreateName?: string;
  customerTypeOptions?: any[];
  customerGroupOptions?: any[];
  countryOptions?: any[];
}

const props = withDefaults(defineProps<Props>(), {
  data: null,
  listCreateName: "",
});

const emit = defineEmits<{
  (e: "close"): void;
  (e: "save", customer: any): void;
}>();

const sendInvoiceViaList = [
  { id: "Print", name: "Print" },
  { id: "Email", name: "Email" },
];

const showAdvanced = ref(false);
const loading = ref(false);

const { fields, formData, setFieldRules, validateForm } =
  useFormValidation({
    id: props.data?.id || "",
    logo: props.data?.logo || "",
    name: props.data?.name || props.listCreateName || "",
    customer_types_id: props.data?.customer_types_id || "",
    customer_groups_id: props.data?.customer_groups_id || "",
    registration_no: props.data?.registration_no || "",
    tax_registration_no: props.data?.tax_registration_no || "",
    address: props.data?.address || "",
    buildings_id: props.data?.buildings_id || "",
    countrys_id: props.data?.countrys_id || "",
    states_id: props.data?.states_id || "",
    citys_id: props.data?.citys_id || "",
    roads_id: props.data?.roads_id || "",
    mobile: props.data?.mobile || "",
    email: props.data?.email || "",
    url: props.data?.url || "",
    price_lists_id: props.data?.price_lists_id || "",
    credit_terms_id: props.data?.credit_terms_id || "",
    payment_types_id: props.data?.payment_types_id || "",
    credit_limit: props.data?.credit_limit || 0,
    sales_reps_id: props.data?.sales_reps_id || "",
    accounts_receivable_id: props.data?.accounts_receivable_id || "",
    send_invoice_via: props.data?.send_invoice_via || "",
    auto_reminder: props.data?.auto_reminder || false,
    first_reminder: props.data?.first_reminder || 0,
    second_reminder: props.data?.second_reminder || 0,
    third_reminder: props.data?.third_reminder || 0,
    active: props.data?.active !== undefined ? props.data.active : true,
    uid: props.data?.uid || "",
    addresses: props.data?.addresses || [],
  });

setFieldRules("name", [required("Name is required")]);
setFieldRules("customer_types_id", [required("Customer Type is required")]);
setFieldRules("mobile", [required("Mobile No is required")]);
setFieldRules("credit_terms_id", [required("Credit Term is required")]);
setFieldRules("credit_limit", [required("Credit Limit is required")]);
setFieldRules("accounts_receivable_id", [
  required("Receivable Account is required"),
]);

const handleCustomerType = (data: any) => {
  if (data) {
    fields.value.price_lists_id.value = data.price_lists_id;
    fields.value.credit_terms_id.value = data.credit_terms_id;
    fields.value.payment_types_id.value = data.payment_types_id;
    fields.value.credit_limit.value = data.credit_limit;
    fields.value.accounts_receivable_id.value = data.accounts_receivable_id;
    fields.value.send_invoice_via.value = data.send_invoice_via;
    fields.value.auto_reminder.value = toBool(data.auto_reminder);
    fields.value.first_reminder.value = data.first_reminder;
    fields.value.second_reminder.value = data.second_reminder;
    fields.value.third_reminder.value = data.third_reminder;
  }
};

const endPoint = "/clients/revenue/master-data/customers/";
const handleSubmit = async () => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (endPoint.slice(-1) == "/" ? endPoint : endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
    }
  }
};
</script>
